'use client';

import {
  DialogBody,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogHeading,
  DialogRoot,
  Spinner,
  Stack,
  Text,
} from '@pie/amber';
import { reporter } from '@upms/observability';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorBody } from '@/components/PaymentDialog/ErrorBody';
import { StripePaymentContent } from '@/components/PaymentDialog/StripePaymentDialogContent';
import {
  isSessionValid,
  isStripeSession,
  PaymentMethodType,
  useInvalidateAccountPaymentMethods,
  useInvalidatePolicyPaymentMethod,
  usePaymentSession,
} from '@/lib/api/billing';
import { usePolicy } from '@/hooks/usePolicy';
import { trackPaymentMethodUpdateOutcome } from '@/lib/analytics/trackPaymentMethodUpdateOutcome';
import { PaymentOutcomeProvider, usePaymentOutcome } from './PaymentOutcomeContext';
import { getPaymentDialogPhase, useTrackPaymentMethodUpdateStarted } from './usePaymentDialogPhase';
import { ZuoraPaymentDialogContent } from './ZuoraPaymentDialogContent/ZuoraPaymentDialogContent';

/**
 * Platform-branched entry for the edit-payment modal. Owns the dialog shell and
 * method-type selection, and fetches a session (`usePaymentSession`, keyed on
 * API method type so Credit/Debit share one). Branches on `platform`: `zuora`
 * renders the hosted form, `stripe` hands off to the Stripe dialog.
 */
export type PaymentDialogProps = {
  policyId: string;
  onClose: () => void;
  /** @deprecated Success is confirmed by the "Done" button inside the dialog; `onClose` is sufficient. */
  onSuccess?: () => void;
  onError?: (message: string) => void;
  hasExistingPaymentMethod?: boolean;
};

// TODO(EE-643): replace with event-driven notification from billing team.
// Zuora replicates to PPS Billing asynchronously; querying immediately returns
// the old method. The 5s wait is a stopgap — show a spinner and block close
// until replication is likely complete, then reveal the success banner.
// Cache invalidation happens when the user closes the modal (PaymentDialog.handleClose),
// not here, so the billing table is only affected after the dialog is gone.
const BILLING_REPLICATION_DELAY_MS = 5_000;

const LoadingBody = () => (
  <DialogBody>
    <Stack align="center" className="pb-layout-xl pt-layout-xl" direction="column" gap="2xs">
      <Spinner className="text-text-subtle-default" size="lg" title="Please wait" />
      <Text emphasis="subtle" variant="body-xs">
        Loading
      </Text>
    </Stack>
  </DialogBody>
);

type PaymentDialogContentProps = PaymentDialogProps & {
  /** Called by the PaymentDialog shell to block close affordances during the replication wait. */
  onAwaitingReplicationChange?: (isAwaiting: boolean) => void;
};

export const PaymentDialogContent = ({
  policyId,
  onClose,
  onSuccess: _onSuccess, // Success dismissal is managed internally after the replication wait
  onError,
  hasExistingPaymentMethod,
  onAwaitingReplicationChange,
}: PaymentDialogContentProps) => {
  const [paymentMethodType, setPaymentMethodType] = useState<PaymentMethodType>('ach');
  const { report: reportOutcome } = usePaymentOutcome();

  const handlePaymentMethodTypeChange = (type: PaymentMethodType) => {
    setPaymentMethodType(type);
    reporter.action('payment_method_selected', {
      feature: 'payment-dialog',
      policyId,
      paymentMethodType: type,
    });
    reportOutcome({ methodCategory: type, errorCategory: 'none' });
  };

  const policyQuery = usePolicy(policyId);
  const {
    data: session,
    isFetching: isSessionFetching,
    isError: sessionIsError,
    isPlaceholderData,
    refetch: refetchSession,
  } = usePaymentSession(policyId, paymentMethodType);

  const [isAwaitingReplication, setIsAwaitingReplication] = useState(false);
  const replicationTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(replicationTimerRef.current), []);

  const handleSuccess = () => {
    clearTimeout(replicationTimerRef.current);
    setIsAwaitingReplication(true);
    onAwaitingReplicationChange?.(true);
    replicationTimerRef.current = setTimeout(() => {
      setIsAwaitingReplication(false);
      onAwaitingReplicationChange?.(false);
    }, BILLING_REPLICATION_DELAY_MS);
  };

  const phase = getPaymentDialogPhase({ policyQuery, session, isSessionFetching, sessionIsError });
  useTrackPaymentMethodUpdateStarted(phase);

  if (phase.kind === 'loading') {
    return <LoadingBody />;
  }

  if (phase.kind === 'error') {
    reportOutcome({ errorCategory: 'session_unavailable' });
    return <ErrorBody />;
  }

  // `phase.kind === 'ready'` already proves this at runtime, but TypeScript's
  // discriminant narrowing of `session` (excluding `undefined` and the
  // sibling `stripe`/`zuora` field below) only follows a check written here,
  // in this same function scope — not one made inside getPaymentDialogPhase.
  if (!isSessionValid(session)) {
    return <ErrorBody />;
  }

  // keepPreviousData keeps the secret populated across a method-switch refetch;
  // `isSessionFetching` drives PaymentForm's spinner.
  if (isStripeSession(session)) {
    return (
      <StripePaymentContent
        session={{ isLoading: isSessionFetching, data: session.stripe }}
        policyId={policyId}
        policyNumber={phase.policyNumber}
        paymentMethodType={paymentMethodType}
        onPaymentMethodTypeChange={handlePaymentMethodTypeChange}
        onSuccess={handleSuccess}
        onClose={onClose}
        isAwaitingReplication={isAwaitingReplication}
        hasExistingPaymentMethod={hasExistingPaymentMethod}
      />
    );
  }

  // While (re)fetching, hand Zuora a null session so its spinner spans the wait.
  const zuoraSession = !isPlaceholderData ? session.zuora : null;

  return (
    <ZuoraPaymentDialogContent
      session={{ isLoading: isSessionFetching, data: zuoraSession }}
      policyId={policyId}
      policyNumber={phase.policyNumber}
      paymentMethodType={paymentMethodType}
      onPaymentMethodTypeChange={handlePaymentMethodTypeChange}
      onRequestNewSession={() => void refetchSession()}
      onClose={onClose}
      onSuccess={handleSuccess}
      isAwaitingReplication={isAwaitingReplication}
      onError={onError}
      hasExistingPaymentMethod={hasExistingPaymentMethod}
    />
  );
};

const PaymentDialogShell = ({
  policyId,
  onClose,
  onSuccess: _onSuccess, // Success dismissal is managed internally after the replication wait
  onError,
  hasExistingPaymentMethod,
}: PaymentDialogProps) => {
  const [isAwaitingReplication, setIsAwaitingReplication] = useState(false);
  // Set to true when the replication timer fires (success banner visible).
  // handleClose uses this to decide whether to invalidate the billing cache.
  const paymentSucceededRef = useRef(false);
  const { read: readOutcome } = usePaymentOutcome();

  useEffect(() => {
    reporter.action('payment_modal_opened', { feature: 'payment-dialog', policyId });
  }, [policyId]);
  const invalidatePolicyPaymentMethod = useInvalidatePolicyPaymentMethod(policyId);
  const invalidateAccountPaymentMethods = useInvalidateAccountPaymentMethods();

  const handleClose = useCallback(() => {
    const shouldInvalidate = paymentSucceededRef.current;
    reporter.action('payment_modal_closed', {
      feature: 'payment-dialog',
      policyId,
      outcome: shouldInvalidate ? 'success' : 'abandoned',
    });

    // Success wins over any earlier failed attempt: the user did finish.
    trackPaymentMethodUpdateOutcome(shouldInvalidate, readOutcome());

    if (shouldInvalidate) {
      invalidatePolicyPaymentMethod();
    }
    onClose();
    if (shouldInvalidate) {
      // Defer so the dialog close animation completes before the refetch starts —
      // otherwise the table opacity effect is hidden behind the dialog overlay.
      setTimeout(() => invalidateAccountPaymentMethods(), 0);
    }
  }, [
    invalidatePolicyPaymentMethod,
    invalidateAccountPaymentMethods,
    onClose,
    policyId,
    readOutcome,
  ]);

  const handleAwaitingReplicationChange = useCallback((isAwaiting: boolean) => {
    setIsAwaitingReplication(isAwaiting);
    if (!isAwaiting) {
      paymentSucceededRef.current = true;
    }
  }, []);

  return (
    <DialogRoot
      open
      onOpenChange={(open) => {
        if (!open && !isAwaitingReplication) handleClose();
      }}
      position={{ base: 'bottom', sm: 'center' }}
      size="lg"
    >
      <DialogContent className="rounded-layout-default">
        <DialogHeader>
          <DialogHeading>Payment Information</DialogHeading>
          <DialogClose />
        </DialogHeader>
        <PaymentDialogContent
          policyId={policyId}
          onClose={handleClose}
          onError={onError}
          hasExistingPaymentMethod={hasExistingPaymentMethod}
          onAwaitingReplicationChange={handleAwaitingReplicationChange}
        />
      </DialogContent>
    </DialogRoot>
  );
};

export const PaymentDialog = (props: PaymentDialogProps) => (
  <PaymentOutcomeProvider>
    <PaymentDialogShell {...props} />
  </PaymentOutcomeProvider>
);
